import React, { ReactElement, ReactNode, useState } from 'react'
import styles from './ContactForm.module.scss'
import { useMatchMedia } from '../../hooks/useMatchMedia'
import { pageBreakpoints } from '../../config/pageBreakpoints'
import { DesktopBodyLarge, DesktopButton, MobileBodyLarge, MobileButton } from '../../Typography'
import { emailClient } from '../../config/axiosConfig'
import { RingLoader } from 'react-spinners'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '../../config/routes'

const predefinedSubjects: string[] = [
  'Ich möchte FIGHTBACK Supporter werden',
  'Ich bin FIGHTBACK Supporter und habe ein Anliegen',
  'Ich möchte in den Ring steigen',
  'Ich möchte Teil der FIGHTBACK-Crew werden',
  'Ich habe Feedback für FIGHTBACK-WRESTLING',
  'Ich möchte Feedback zur Website geben',
  'Ich habe eine Frage',
  'Freie Betreff Eingabe…',
]

interface LabelProps {
  children: ReactNode
}

export const ContactForm = (): ReactElement => {
  const location = useLocation()
  const initialSubject = location.state || ''

  const isMobile = useMatchMedia(`(max-width:${pageBreakpoints.md}`)
  const [name, setName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [subject, setSubject] = useState<string>(initialSubject)
  const [customSubject, setCustomSubject] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()

  const ownSubject = predefinedSubjects.at(-1)

  const Label = ({ children }: LabelProps): ReactElement => {
    return isMobile ? (
      <MobileBodyLarge fontWeight={600}>{children}</MobileBodyLarge>
    ) : (
      <DesktopBodyLarge fontWeight={600}>{children}</DesktopBodyLarge>
    )
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    if (!isLoading) {
      e.preventDefault()
      setError('')

      const finalSubject = subject === ownSubject ? customSubject : subject

      if (!name) {
        setError('Bitte gib einen Namen an.')
        return
      }

      if (!validateEmail(email)) {
        setError('Bitte gib eine korrekte E-Mail Adresse ein')
        return
      }

      if (!finalSubject) {
        setError('Bitte wähle einen Betreff aus.')
        return
      }

      setIsLoading(true)
      try {
        await sendEmail(`Kontaktformular: ${finalSubject}`)
        alert('Vielen Dank! Deine E-Mail wurde erfolgreich gesendet.')
        navigate(ROUTES.start)
      } catch (error) {
        setError(
          'Senden fehlgeschlagen. Bitte überprüfe deine Eingaben und versuche es gegebenenfalls zu einem späteren Zeitpunkt erneut.',
        )
      } finally {
        setIsLoading(false)
      }
    }
  }

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const sendEmail = async (finalSubject: string): Promise<void> => {
    return emailClient.post('', {
      name: name,
      email: email,
      subject: finalSubject,
      message: message,
    })
  }

  return (
    <div className={styles.contactForm}>
      <div className={styles.formGroup}>
        <Label>Deine Name:</Label>
        <input
          id='name'
          value={name}
          onChange={(e): void => setName(e.target.value)}
          required
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <Label>Deine E-Mail Adresse:</Label>
        <input
          type='email'
          id='email'
          value={email}
          onChange={(e): void => setEmail(e.target.value)}
          required
          className={styles.input}
        />
      </div>

      <div className={styles.formGroup}>
        <Label>Betreff:</Label>
        <select
          id='subject'
          value={subject}
          onChange={(e): void => setSubject(e.target.value)}
          className={styles.input}
        >
          <option value='' disabled>
            Wähle einen Betreff
          </option>
          {predefinedSubjects.map((sub) => (
            <option key={sub} value={sub}>
              {sub}
            </option>
          ))}
        </select>
      </div>

      {subject === ownSubject && (
        <div className={styles.formGroup}>
          <Label>Dein eigener Betreff:</Label>
          <input
            type='text'
            id='customSubject'
            value={customSubject}
            onChange={(e): void => setCustomSubject(e.target.value)}
            required
            className={styles.input}
          />
        </div>
      )}

      <div className={styles.formGroup}>
        <Label>Deine Nachricht an uns:</Label>
        <textarea
          id='message'
          value={message}
          onChange={(e): void => setMessage(e.target.value)}
          required
          className={styles.textarea}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.button} onClick={handleSubmit}>
        {isLoading ? (
          <RingLoader size={'1rem'} color={'#010101'} />
        ) : isMobile ? (
          <MobileButton darkFont={true}>Senden</MobileButton>
        ) : (
          <DesktopButton darkFont={true}>Senden</DesktopButton>
        )}
      </div>
    </div>
  )
}
